/**
 * A project's details (R209, MB 4 Nov 2026; docs/specs/project-details.md): its title, client name, area and state,
 * edited in Teslyn Cloud (the project's Set-up tab) and Teslyn Field (Project settings). Until R209 they came only
 * from where the project was made (the folder name, a TJR job import or a signed contract) and nothing could change
 * them, so F26044's "Kimberly" stayed misspelt and decision 1017's height breach report name had no box to fill.
 *
 * The survey number is read only after the project is made: it names the project folder, it is unique in the
 * company (unique (tenant_id, number)), TJR imports and Teslyn Cloud match a project by it, and the Geosoft scripts,
 * report names and contracts carry it. Editing the details never renames the project folder on disk.
 *
 * Who may change them: the project's writers in Teslyn Cloud (policy `project_write`: tenant admins, managers and
 * office staff, decision 27), as for the rest of Project settings; a local workspace (not signed in) always. An
 * archived project is read only (decision 418, `project_lifecycle_guard`). Every change is audited in Teslyn Cloud
 * (`project.details_changed`, 20261104209000_project_details.sql).
 */
import { heightBreachJobName } from './height-breach.js';
import type { Project, ValidationIssue } from './model.js';

/** The details that can be changed, in the order the forms show them. */
export const PROJECT_DETAILS_FIELDS = ['title', 'client_name', 'area', 'state'] as const;
export type ProjectDetailsField = (typeof PROJECT_DETAILS_FIELDS)[number];
export type ProjectDetails = Readonly<Record<ProjectDetailsField, string>>;

export const PROJECT_DETAIL_LABEL: Readonly<Record<ProjectDetailsField | 'number', string>> = {
  number: 'Survey number',
  title: 'Title',
  client_name: 'Client name',
  area: 'Area',
  state: 'State',
};

/** One line under each box: where the value is used (the forms and the help say the same). */
export const PROJECT_DETAIL_USES: Readonly<Record<ProjectDetailsField | 'number', string>> = {
  number:
    'Set when the project was made. It names the project folder, and project file imports and Teslyn Cloud match the project by it, so it is not changed here.',
  title:
    'Headings in Teslyn Field and Teslyn Cloud after the number, the Projects list, the client portal, report emails, the JSA, JSP, pre-flight briefs and the daily report cover.',
  client_name:
    'The height breach report name, the daily report header and cover, "Client" on the JSA, JSP and pre-flight briefs, and the Refly list heading. Separate from the client organisation, which decides who sees the project in the client portal.',
  area: 'The height breach report name, "Area" on the JSP, the Refly list heading and Export project to TJR.',
  state: 'After the area on the JSP and in the Refly list heading, and in Export project to TJR.',
};

/** The longest each detail may be, in characters. */
export const PROJECT_DETAIL_MAX: Readonly<Record<ProjectDetailsField, number>> = {
  title: 120,
  client_name: 120,
  area: 120,
  state: 40,
};

/** Who may change the details signed in: the project's writers (policy `project_write`). */
export const PROJECT_DETAILS_EDITORS = ['tenant_admin', 'manager', 'office_staff'] as const;

/** The details of a project as stored. */
export function projectDetailsOf(p: Pick<Project, ProjectDetailsField>): ProjectDetails {
  return { title: p.title ?? '', client_name: p.client_name ?? '', area: p.area ?? '', state: p.state ?? '' };
}

/** The details as saved: spaces at the ends removed and runs of spaces made one. */
export function cleanProjectDetails(d: ProjectDetails): ProjectDetails {
  const clean = (s: string): string => s.replace(/\s+/g, ' ').trim();
  return {
    title: clean(d.title),
    client_name: clean(d.client_name),
    area: clean(d.area),
    state: clean(d.state),
  };
}

/** What stops a save, in words: a title is needed; each detail has a longest length. */
export function projectDetailsIssues(d: ProjectDetails): ValidationIssue[] {
  const c = cleanProjectDetails(d);
  const issues: ValidationIssue[] = [];
  if (c.title === '')
    issues.push({ field: 'title', message: 'Give the project a title, for example Kimberley.' });
  for (const f of PROJECT_DETAILS_FIELDS) {
    if (c[f].length > PROJECT_DETAIL_MAX[f]) {
      issues.push({
        field: f,
        message: `${PROJECT_DETAIL_LABEL[f]} is ${c[f].length} characters: keep it to ${PROJECT_DETAIL_MAX[f]}.`,
      });
    }
  }
  return issues;
}

/** Hints that do not stop a save: a title that starts with the survey number shows the number twice in headings. */
export function projectDetailsHints(number: string, d: ProjectDetails): string[] {
  const n = number.trim();
  const t = cleanProjectDetails(d).title;
  if (n !== '' && t.toLowerCase().startsWith(n.toLowerCase())) {
    return [`Headings put the number before the title ("${n} ${t}"): leave ${n} out of the title.`];
  }
  return [];
}

/** The details that a save would change (compared as saved). */
export function projectDetailsChanged(before: ProjectDetails, after: ProjectDetails): ProjectDetailsField[] {
  const a = cleanProjectDetails(before);
  const b = cleanProjectDetails(after);
  return PROJECT_DETAILS_FIELDS.filter((f) => a[f] !== b[f]);
}

/**
 * Whether the person may change the details: an archived project never (read only, decision 418); a local workspace
 * always; signed in, a tenant admin, manager or office staff member; roles not known yet, no.
 */
export function projectDetailsEditable(
  roles: readonly string[] | null,
  opts: { readonly localWorkspace: boolean; readonly status: string },
): boolean {
  if (opts.status === 'archived') return false;
  if (opts.localWorkspace) return true;
  return roles !== null && roles.some((r) => (PROJECT_DETAILS_EDITORS as readonly string[]).includes(r));
}

/** Why the details are shown read only, in words (null when they can be changed). */
export function projectDetailsReadOnlyWhy(
  roles: readonly string[] | null,
  opts: { readonly localWorkspace: boolean; readonly status: string },
): string | null {
  if (projectDetailsEditable(roles, opts)) return null;
  if (opts.status === 'archived') return 'The project is archived: restore it to change its details.';
  return 'A tenant admin, manager or office staff member changes the project details.';
}

/** The words after a save. */
export function projectDetailsSavedWords(changed: readonly ProjectDetailsField[]): string {
  if (changed.length === 0) return 'Nothing to save: the project details are as they were.';
  const names = changed.map((f, i) =>
    i === 0 ? PROJECT_DETAIL_LABEL[f] : PROJECT_DETAIL_LABEL[f].toLowerCase(),
  );
  const list =
    names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
  return `Saved the project details (${list}). They reach Teslyn Field and Teslyn Cloud with the next sync; the project folder keeps its name.`;
}

/**
 * The height breach report's job name these details give (decision 1017), for the line under the form:
 * `<number>_<client>_<area>`, or the folder's name when all three are empty.
 */
export function projectDetailsBreachName(
  p: Pick<Project, 'number'> & { readonly folder_path?: string | null },
  d: ProjectDetails,
): string {
  const c = cleanProjectDetails(d);
  const { job, fromFolder } = heightBreachJobName({
    number: p.number,
    client_name: c.client_name,
    area: c.area,
    folder_path: p.folder_path ?? null,
  });
  return fromFolder
    ? `The height breach report is named after the project folder (${job}) until the client name or area is filled in.`
    : `The height breach report is named ${job}_Working_Height_Breach_Report_<date>.`;
}
